/**
 * Whether the API has told us this browser's session is over.
 *
 * The API renews the access token underneath every request, so a 401 from a
 * browser that held a session means the session itself has gone: lifetime
 * reached, ended by an administrator, or a refused renewal. Retrying asks the
 * same question twice. A module-level flag, observed the way the pilot refusal
 * is, because the API client has no component to raise it from.
 */

let expired = false;
let signedIn = false;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

/** Record that the session has gone. Idempotent. */
export function markSessionExpired(): void {
  if (expired) return;
  expired = true;
  announce();
}

/** Forget it, so a fresh sign-in does not meet the last session's ending. */
export function clearSessionExpired(): void {
  signedIn = false;
  if (!expired) return;
  expired = false;
  announce();
}

/** Record that this page has been told who is signed in. */
export function noteSignedIn(): void {
  signedIn = true;
}

/** Whether this page held a session to lose: it signed in, or still carries the readable cookie, which lapses with the other. */
export function heldASession(hasCsrfCookie: boolean): boolean {
  return signedIn || hasCsrfCookie;
}

export function isSessionExpired(): boolean {
  return expired;
}

export function subscribeSessionExpired(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Whether a failed response says the session has gone.
 *
 * Status alone is not enough: `/auth/me` answers 401 to every signed-out
 * visitor, so the caller pairs this with evidence that a session existed.
 */
export function isSessionRefusal(status: number): boolean {
  return status === 401;
}
